import { useCallback, useEffect, useRef, useState } from 'react'

// Web Speech API types (not yet in TypeScript's standard lib)
interface SpeechRecognitionResult {
  readonly isFinal: boolean
  readonly length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}
interface SpeechRecognitionAlternative {
  readonly transcript: string
  readonly confidence: number
}
interface SpeechRecognitionResultList {
  readonly length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}
interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string
}
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onend: (() => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance

interface UseSpeechToTextOptions {
  onFinalTranscript: (text: string) => void
  lang?: string
}

interface UseSpeechToTextReturn {
  isListening: boolean
  isSupported: boolean
  interimTranscript: string
  error: string | null
  startListening: () => void
  stopListening: () => void
}

// Map Web Speech API error codes to user-facing copy. `no-speech` is a
// recoverable silence timeout and intentionally not surfaced here.
function errorMessageFor(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return "Microphone access was blocked. Enable it in your browser's site settings, then try again."
    case 'audio-capture':
      return 'No microphone was found. Check that one is connected and try again.'
    case 'network':
      return 'Voice input lost connection. Check your network and try again.'
    default:
      return 'Voice input stopped unexpectedly. Please try again.'
  }
}

type AugmentedWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor
  webkitSpeechRecognition?: SpeechRecognitionConstructor
}

function getSpeechRecognitionAPI(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined
  const w = window as AugmentedWindow
  return w.SpeechRecognition ?? w.webkitSpeechRecognition
}

export function useSpeechToText({
  onFinalTranscript,
  lang = 'en-US',
}: UseSpeechToTextOptions): UseSpeechToTextReturn {
  const SpeechRecognitionAPI = getSpeechRecognitionAPI()
  const isSupported = SpeechRecognitionAPI != null
  const apiRef = useRef(SpeechRecognitionAPI)

  const [isListening, setIsListening] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const shouldRestartRef = useRef(false)

  // Keep callbacks in a ref so recognition handlers always call the latest version
  const callbacksRef = useRef({ onFinalTranscript })
  useEffect(() => {
    callbacksRef.current = { onFinalTranscript }
  })

  const stopListening = useCallback(() => {
    shouldRestartRef.current = false
    setIsListening(false)
    setInterimTranscript('')
    setError(null)
    recognitionRef.current?.stop()
  }, [])

  const startListening = useCallback(() => {
    const API = apiRef.current
    if (!API) return

    shouldRestartRef.current = true
    setIsListening(true)
    setError(null)

    const recognition: SpeechRecognitionInstance = new API()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = lang

    recognition.onresult = (event: SpeechRecognitionEvent): void => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (!result) continue
        const transcript = result[0]?.transcript ?? ''
        if (result.isFinal) {
          setInterimTranscript('')
          callbacksRef.current.onFinalTranscript(transcript)
        } else {
          interim += transcript
        }
      }
      // Replace interim display — not accumulated, just the current in-progress phrase
      setInterimTranscript(interim)
    }

    // Chrome stops after silence; restart if user hasn't clicked stop
    recognition.onend = () => {
      if (shouldRestartRef.current) {
        recognition.start()
      } else {
        setIsListening(false)
      }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent): void => {
      // no-speech = silence timeout — recoverable, keep listening silently.
      // Everything else (mic denied, no device, network) is surfaced to the user.
      if (event.error !== 'no-speech') {
        shouldRestartRef.current = false
        setIsListening(false)
        setInterimTranscript('')
        setError(errorMessageFor(event.error))
      }
    }

    recognitionRef.current = recognition
    recognition.start()
  }, [lang])

  // Stop on unmount
  useEffect(() => {
    return () => {
      shouldRestartRef.current = false
      recognitionRef.current?.stop()
    }
  }, [])

  return { isListening, isSupported, interimTranscript, error, startListening, stopListening }
}
